import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDemo } from "../context/DemoContext";
import { useAuth } from "../context/AuthContext";
import { SearchReal } from "../real/RealScreens";
import { FixtureCard } from "../components/FixtureCard";
import { EmptyState } from "../components/EmptyState";

export function SearchFixtures() {
  const { fixtures, isDemo } = useDemo();
  const { configured } = useAuth();
  const realMode = configured && !isDemo;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get("query") ?? "");
  const [date, setDate] = useState(searchParams.get("date") ?? "");
  const [competition, setCompetition] = useState(searchParams.get("competition") ?? "");
  const [selectedFixtureId, setSelectedFixtureId] = useState(searchParams.get("fixture") ?? "");

  const competitions = useMemo(
    () => [...new Set(fixtures.map((f) => f.competitionName))],
    [fixtures]
  );

  const filteredFixtures = useMemo(() => {
    let result = fixtures.filter((f) => f.status === "scheduled" && new Date(f.kickoffAt) > new Date());

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      result = result.filter(
        (f) =>
          f.homeTeamName.toLowerCase().includes(q) ||
          f.awayTeamName.toLowerCase().includes(q) ||
          f.competitionName.toLowerCase().includes(q)
      );
    }

    if (date) {
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);
      const nextDate = new Date(targetDate);
      nextDate.setDate(nextDate.getDate() + 1);
      result = result.filter((f) => {
        const kickoff = new Date(f.kickoffAt);
        return kickoff >= targetDate && kickoff < nextDate;
      });
    }

    if (competition) {
      result = result.filter((f) => f.competitionName === competition);
    }

    return result;
  }, [fixtures, query, date, competition]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("query", query.trim());
    if (date) params.set("date", date);
    if (competition) params.set("competition", competition);
    setSearchParams(params);
  };

  const handleFixtureSelect = (fixtureId: string) => {
    setSelectedFixtureId(fixtureId);
    const params = new URLSearchParams(searchParams);
    params.set("fixture", fixtureId);
    setSearchParams(params);
    navigate(`/report/${fixtureId}`);
  };

  if (realMode) {
    return <SearchReal />;
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Nova Análise</h1>
      </header>

      <form onSubmit={handleSearch} className="card p-4 mb-6" aria-label="Busca de partidas">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <label htmlFor="query" className="label">Buscar times, competição</label>
            <input
              id="query"
              type="search"
              className="input"
              placeholder="Ex: São Paulo, Palmeiras, Brasileirão..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="date" className="label">Data</label>
            <input
              id="date"
              type="date"
              className="input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
          </div>

          <div>
            <label htmlFor="competition" className="label">Competição</label>
            <select id="competition" className="input" value={competition} onChange={(e) => setCompetition(e.target.value)}>
              <option value="">Todas</option>
              {competitions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn btn-primary flex-1">Buscar</button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setQuery("");
              setDate("");
              setCompetition("");
              setSearchParams({});
            }}
          >
            Limpar
          </button>
        </div>
      </form>

      {selectedFixtureId && (
        <div className="alert alert-info mb-6" role="alert">
          <strong>Partida selecionada:</strong>{" "}
          {fixtures.find((f) => f.id === selectedFixtureId)?.homeTeamName} vs{" "}
          {fixtures.find((f) => f.id === selectedFixtureId)?.awayTeamName}
          <button
            type="button"
            className="btn btn-sm btn-ghost ml-4"
            onClick={() => {
              setSelectedFixtureId("");
              const params = new URLSearchParams(searchParams);
              params.delete("fixture");
              setSearchParams(params);
            }}
          >
            Limpar seleção
          </button>
        </div>
      )}

      <section aria-labelledby="results-heading">
        <h2 id="results-heading" className="text-lg font-semibold mb-4">
          Resultados ({filteredFixtures.length})
        </h2>

        {filteredFixtures.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredFixtures.map((fixture) => (
              <FixtureCard
                key={fixture.id}
                fixture={fixture}
                onAnalyze={() => handleFixtureSelect(fixture.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Search />}
            title="Nenhuma partida encontrada"
            text="Tente ajustar os filtros de busca ou verifique se há partidas agendadas para o período selecionado."
          />
        )}
      </section>

      <div className="mt-8 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
        <h3 className="font-semibold mb-2">Como funciona a busca</h3>
        <ul className="text-sm text-[var(--color-text-muted)] space-y-1">
          <li>• Busca por nome de time (mandante ou visitante) ou competição</li>
          <li>• Filtro por data exibe partidas do dia selecionado no fuso America/Sao_Paulo</li>
          <li>• Se houver mais de uma partida, confirme a competição, data e mandante antes de analisar</li>
          <li>• Apenas partidas não iniciadas (status "scheduled") são exibidas</li>
        </ul>
      </div>
    </div>
  );
}
